/**
 * 1.4.0 asset groups' data hooks: group types, groups and their members, against the routes in
 * docs/dev/groups-1.4.0-build.md. Step 0 wrote them so G2 can build on them while G1 builds the routes; G1 owns the
 * file afterwards (fix a hook here when the route turns out different; tell G2). Keys: queryKeys.groupTypes(),
 * queryKeys.groups() and below.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssetGroupDetailDTO, AssetGroupDTO, GroupCreate, GroupListDTO, GroupMembersAddResult, GroupMembersPageDTO, GroupPatch,
  GroupTypeCreate, GroupTypeDTO, GroupTypeListDTO, GroupTypePatch,
} from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';

/** Bodies as the client sends them (the contracts' zod inputs: defaults may be left out). */
type In<T> = Partial<T>;

function useInvalidateGroups() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.groups() });
    void qc.invalidateQueries({ queryKey: queryKeys.assets() });
    void qc.invalidateQueries({ queryKey: queryKeys.groupedAll() });
    void qc.invalidateQueries({ queryKey: ['asset'] });
  };
}

// ---------------------------------------------------------------- group types

export function useGroupTypes(enabled = true) {
  return useQuery({ queryKey: queryKeys.groupTypes(), queryFn: () => api<GroupTypeListDTO>('/api/v1/group-types'), enabled, staleTime: 60_000 });
}
export function useCreateGroupType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: In<GroupTypeCreate> & Pick<GroupTypeCreate, 'key' | 'label' | 'pluralLabel'>) => api<GroupTypeDTO>('/api/v1/group-types', { method: 'POST', json: body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.groupTypes() }),
  });
}
export function usePatchGroupType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: In<GroupTypePatch> }) => api<GroupTypeDTO>(`/api/v1/group-types/${id}`, { method: 'PATCH', json: body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.groupTypes() }),
  });
}
export function useDeleteGroupType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/v1/group-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.groupTypes() }),
  });
}

// ---------------------------------------------------------------- groups

export interface GroupListParams { type?: string; parent?: 'root' | string; q?: string }
const listParams = (p: GroupListParams) => {
  const s = new URLSearchParams();
  if (p.type) s.set('type', p.type);
  if (p.parent) s.set('parent', p.parent);
  if (p.q?.trim()) s.set('q', p.q.trim());
  return s.toString();
};
/** GET /api/v1/groups (keyset pages; Load more). */
export function useGroups(p: GroupListParams, enabled = true) {
  const params = listParams(p);
  return usePaged<AssetGroupDTO, GroupListDTO>({ queryKey: queryKeys.groupList(params), url: `/api/v1/groups?${params}`, enabled, pageSize: 50 });
}
export function useGroup(id: string | null | undefined) {
  return useQuery({ queryKey: queryKeys.group(id), queryFn: () => api<AssetGroupDetailDTO>(`/api/v1/groups/${id}`), enabled: Boolean(id) });
}
export function useCreateGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (body: In<GroupCreate> & Pick<GroupCreate, 'typeId' | 'title'>) => api<AssetGroupDetailDTO>('/api/v1/groups', { method: 'POST', json: body }),
    onSuccess: () => invalidate(),
  });
}
export function usePatchGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GroupPatch }) => api<AssetGroupDetailDTO>(`/api/v1/groups/${id}`, { method: 'PATCH', json: body }),
    onSuccess: () => invalidate(),
  });
}
export function useDeleteGroup() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, children }: { id: string; children?: 'move_up' | 'delete' }) =>
      api<void>(`/api/v1/groups/${id}${children ? `?children=${children}` : ''}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------- members

/** GET /api/v1/groups/:id/members, in the group's order (keyset pages). */
export function useGroupMembers(id: string | null | undefined) {
  return usePaged<GroupMembersPageDTO['items'][number], GroupMembersPageDTO>({ queryKey: queryKeys.groupMembers(id), url: `/api/v1/groups/${id}/members`, enabled: Boolean(id), pageSize: 200 });
}
export function useAddGroupMembers() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, assetIds, position, move }: { id: string; assetIds: string[]; position?: number; move?: boolean }) =>
      api<GroupMembersAddResult>(`/api/v1/groups/${id}/members`, { method: 'POST', json: { assetIds, position, move } }),
    onSuccess: () => invalidate(),
  });
}
export function useRemoveGroupMembers() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, assetIds }: { id: string; assetIds: string[] }) => api<{ removed: number }>(`/api/v1/groups/${id}/members/remove`, { method: 'POST', json: { assetIds } }),
    onSuccess: () => invalidate(),
  });
}
export function useOrderGroupMembers() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, assetIds, renumber }: { id: string; assetIds: string[]; renumber?: boolean }) =>
      api<void>(`/api/v1/groups/${id}/members/order`, { method: 'PATCH', json: { assetIds, renumber } }),
    onSuccess: () => invalidate(),
  });
}
export function usePatchGroupMember() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, assetId, number }: { id: string; assetId: string; number: number | null }) =>
      api<void>(`/api/v1/groups/${id}/members/${assetId}`, { method: 'PATCH', json: { number } }),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------- the uploaded poster (G1's routes)

/** PUT /api/v1/groups/:id/poster: a PNG, JPEG or WebP image (at most 10 MB) as the group's poster. */
export function useUploadGroupPoster() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: Blob }) =>
      api<AssetGroupDetailDTO>(`/api/v1/groups/${id}/poster`, { method: 'PUT', body: file, headers: { 'content-type': file.type || 'application/octet-stream' } }),
    onSuccess: () => invalidate(),
  });
}
/** DELETE /api/v1/groups/:id/poster: no poster (uploaded or a member's). */
export function useRemoveGroupPoster() {
  const invalidate = useInvalidateGroups();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/v1/groups/${id}/poster`, { method: 'DELETE' }),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------- the Library search's suggestions

/** One of GET /api/v1/groups/suggest's items: "Series: American Horror Story", applying ?group=. */
export interface GroupSuggestion {
  groupId: string; typeId: string; typeLabel: string; title: string; path: string[]; label: string; memberCount: number; filter: { group: string };
}
/** Groups whose titles match the Library's search text (two characters at least). */
export function useGroupSuggestions(q: string) {
  const text = q.trim();
  return useQuery({
    queryKey: queryKeys.groupSuggestions(text),
    queryFn: () => api<{ items: GroupSuggestion[] }>(`/api/v1/groups/suggest?${new URLSearchParams({ q: text })}`),
    enabled: text.length >= 2,
    staleTime: 30_000,
  });
}
